import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { useSettings } from './SettingsContext';

interface ScopaAnimationProps {
  scopaEvent: { playerIndex: number; timestamp: number } | null;
  scopaParticleSeeds: { x: number; y: number; scale: number; rotate: number; duration: number }[];
  opponentName?: string;
  myPlayerIndex?: number;
}

export default function ScopaAnimation({ scopaEvent, scopaParticleSeeds, opponentName, myPlayerIndex = 0 }: ScopaAnimationProps) {
  const { playSfx, triggerHaptic } = useSettings();
  const isMine = scopaEvent ? scopaEvent.playerIndex === myPlayerIndex : false;
  const displayName = isMine ? 'PER TE' : `PER ${opponentName || 'LA CPU'}`;

  useEffect(() => {
    if (scopaEvent) {
      playSfx('scopa');
      if (isMine) {
        triggerHaptic('success');
      } else {
        triggerHaptic('medium');
      }
    }
  }, [scopaEvent?.timestamp, isMine, playSfx, triggerHaptic]);

  return (
    <AnimatePresence>
      {scopaEvent && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
          className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none overflow-hidden"
        >
          {/* animated background flash */}
          <motion.div
            initial={{ opacity: 0.8 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className={cn("absolute inset-0 backdrop-blur-sm", isMine ? 'bg-yellow-500/30' : 'bg-red-500/30')}
          />

          {/* Main text */}
          <motion.div
            initial={{ scale: 0.5, y: 50, rotate: -15, opacity: 0 }}
            animate={{ scale: 1, y: 0, rotate: 0, opacity: 1 }}
            transition={{ type: "spring", bounce: 0.7, duration: 0.8 }}
            className="relative flex flex-col items-center z-10"
          >
            <motion.span
              className={cn(
                "text-7xl sm:text-9xl font-black italic tracking-tighter text-transparent bg-clip-text drop-shadow-[0_0_50px_rgba(234,179,8,0.8)]",
                isMine
                  ? "bg-gradient-to-br from-yellow-300 via-yellow-500 to-amber-600"
                  : "bg-gradient-to-br from-red-400 via-red-600 to-rose-800 drop-shadow-[0_0_50px_rgba(225,29,72,0.8)]"
              )}
              style={{ WebkitTextStroke: '2px rgba(255,255,255,0.5)' }}
            >
              SCOPA!
            </motion.span>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={cn(
                "mt-4 px-6 py-2 bg-black/50 backdrop-blur-md rounded-full border tracking-widest text-lg sm:text-2xl font-black",
                isMine ? "border-yellow-500/30 text-yellow-100" : "border-red-500/30 text-red-100"
              )}
            >
              +1 PUNTO {displayName}
            </motion.div>
          </motion.div>

          {/* simple particles */}
          {Array.from({ length: 40 }).map((_, i) => (
            <motion.div
              key={i}
              initial={{
                opacity: 1,
                x: scopaParticleSeeds[i].x * (typeof window !== 'undefined' ? window.innerWidth : 800),
                y: scopaParticleSeeds[i].y * (typeof window !== 'undefined' ? window.innerHeight : 600),
                scale: scopaParticleSeeds[i].scale,
                rotate: 0
              }}
              animate={{
                opacity: 0,
                x: scopaParticleSeeds[i].x * (typeof window !== 'undefined' ? window.innerWidth : 800) + (scopaParticleSeeds[i].x * 400 - 200),
                y: scopaParticleSeeds[i].y * (typeof window !== 'undefined' ? window.innerHeight : 600) + (scopaParticleSeeds[i].y * 300 - 150),
                rotate: scopaParticleSeeds[i].rotate
              }}
              transition={{ duration: scopaParticleSeeds[i].duration, ease: "easeOut" }}
              className="absolute z-0 w-8 h-8 flex items-center justify-center text-3xl drop-shadow-lg"
            >
              {scopaEvent.playerIndex === 0 ? ['⭐','🌟','✨','🟡','🔥'][i % 5] : ['💥','💢','🔺','🔥','♦️'][i % 5]}
            </motion.div>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}